import React, { useState } from 'react';
import { ClipboardCheckIcon, TargetIcon, CheckCircle2Icon, ClockIcon, EyeIcon, ZapIcon, ArrowRightIcon, SparklesIcon } from 'lucide-react';
export function HowItWorks() {
  const [activeStep, setActiveStep] = useState(0);
  const steps = [{
    number: '01',
    icon: <ClipboardCheckIcon className="h-8 w-8 md:h-10 md:w-10" data-id="element-1153" />,
    title: 'Un solo formulario',
    description: 'Completa un formulario simple. Con eso evaluamos tu perfil en múltiples instituciones financieras simultáneamente.',
    // Mobile: versión ultra resumida
    mobileTitle: 'Un formulario',
    mobileDescription: 'Evaluamos tu perfil en múltiples bancos',
    stat: '3 sem',
    statLabel: 'ahorradas',
    benefit: 'Ahorro de tiempo',
    benefitIcon: <ClockIcon className="h-6 w-6" data-id="element-1154" />,
    benefitText: 'De semanas de trámites a minutos de evaluación',
    color: 'from-[#2B5FD9] to-[#1E4BB8]',
    lightColor: 'bg-blue-50',
    darkColor: 'text-[#2B5FD9]'
  }, {
    number: '02',
    icon: <TargetIcon className="h-8 w-8 md:h-10 md:w-10" data-id="element-1155" />,
    title: 'Resultados claros',
    description: 'Simulamos cuánto podrías pedir, estimamos tu dividendo, y te mostramos alternativas reales según tu perfil.',
    mobileTitle: 'Resultados claros',
    mobileDescription: 'Monto, dividendo y alternativas reales',
    stat: '5+',
    statLabel: 'bancos',
    benefit: 'Claridad total',
    benefitIcon: <EyeIcon className="h-6 w-6" data-id="element-1156" />,
    benefitText: 'Resultados claros, comparables y alineados a tu perfil',
    color: 'from-purple-600 to-purple-500',
    lightColor: 'bg-purple-50',
    darkColor: 'text-purple-600'
  }, {
    number: '03',
    icon: <CheckCircle2Icon className="h-8 w-8 md:h-10 md:w-10" data-id="element-1157" />,
    title: 'Decides informado',
    description: 'Todo en un solo lugar, con total claridad, para que decidas informado y avances más rápido hacia tu nuevo hogar.',
    mobileTitle: 'Decides informado',
    mobileDescription: 'Todo en un lugar, avanza más rápido',
    stat: '100%',
    statLabel: 'gratis',
    benefit: 'IA personal',
    benefitIcon: <ZapIcon className="h-6 w-6" data-id="element-1158" />,
    benefitText: 'Como tener un analista hipotecario impulsado por IA',
    color: 'from-green-600 to-green-500',
    lightColor: 'bg-green-50',
    darkColor: 'text-green-600'
  }];
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section id="como-funciona" className="py-12 md:py-24 bg-gradient-to-b from-white via-[#F8FAFC] to-white relative overflow-hidden" data-id="element-1159">
      {/* Decoraciones de fondo */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#2B5FD9]/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" data-id="element-1160" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" data-id="element-1161" />

      <div className="container mx-auto max-w-7xl px-4 relative z-10" data-id="element-1162">
        {/* Header */}
        <div className="text-center mb-6 md:mb-16" data-id="element-1163">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2B5FD9]/10 rounded-full mb-3 md:mb-4" data-id="element-1164">
            <SparklesIcon className="h-4 w-4 text-[#2B5FD9]" data-id="element-1165" />
            <span className="text-sm font-semibold text-[#2B5FD9]" data-id="element-1166">
              Simple y rápido
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold mb-2 md:mb-4 text-[#0A1628]" data-id="element-1167">
            Cómo funciona Creditual
          </h2>
          <p className="text-sm md:text-lg text-[#6B7280] max-w-2xl mx-auto px-4" data-id="element-1168">
            Tres pasos simples para tu crédito hipotecario
          </p>
        </div>

        {/* Desktop: Journey visual */}
        <div className="hidden md:block" data-id="element-1169">
          <div className="relative" data-id="element-1170">
            {/* Línea conectora */}
            <div className="absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-[#2B5FD9] via-purple-500 to-green-500 opacity-20" data-id="element-1171" />

            <div className="grid grid-cols-3 gap-8" data-id="element-1172">
              {steps.map((step, index) => <div key={index} className="relative" onMouseEnter={() => setActiveStep(index)} data-id="element-1173">
                  {/* Card principal */}
                  <div className={`group relative bg-white rounded-2xl p-8 shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-transparent hover:-translate-y-2 ${activeStep === index ? 'shadow-2xl -translate-y-2' : ''}`} data-id="element-1174">
                    {/* Gradiente de fondo al hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`} data-id="element-1175" />

                    {/* Número del paso */}
                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center border-4 border-white" data-id="element-1176">
                      <span className={`text-xl font-bold bg-gradient-to-br ${step.color} bg-clip-text text-transparent`} data-id="element-1177">
                        {step.number}
                      </span>
                    </div>

                    {/* Icono principal */}
                    <div className="relative mb-6" data-id="element-1178">
                      <div className={`relative w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${step.color} p-0.5 shadow-lg group-hover:shadow-xl transition-all duration-500 group-hover:scale-110`} data-id="element-1179">
                        <div className={`w-full h-full ${step.lightColor} rounded-2xl flex items-center justify-center ${step.darkColor}`} data-id="element-1180">
                          {step.icon}
                        </div>
                      </div>
                    </div>

                    {/* Contenido del paso */}
                    <div className="relative text-center mb-6" data-id="element-1181">
                      <h3 className="text-xl font-bold mb-3 text-[#0A1628] group-hover:text-transparent group-hover:bg-gradient-to-br group-hover:bg-clip-text group-hover:from-[#0A1628] group-hover:to-[#2B5FD9] transition-all" data-id="element-1182">
                        {step.title}
                      </h3>
                      <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-1183">
                        {step.description}
                      </p>
                    </div>

                    {/* Separador */}
                    <div className="relative mb-6" data-id="element-1184">
                      <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" data-id="element-1185" />
                    </div>

                    {/* Beneficio */}
                    <div className={`relative ${step.lightColor} rounded-xl p-4 border border-gray-100`} data-id="element-1186">
                      <div className="flex items-center gap-3 mb-2" data-id="element-1187">
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${step.color} flex items-center justify-center text-white`} data-id="element-1188">
                          {step.benefitIcon}
                        </div>
                        <div className="flex-1" data-id="element-1189">
                          <p className={`text-sm font-bold ${step.darkColor} mb-1`} data-id="element-1190">
                            {step.benefit}
                          </p>
                          <p className="text-xs text-[#6B7280]" data-id="element-1191">
                            {step.benefitText}
                          </p>
                        </div>
                      </div>

                      {/* Stat */}
                      <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-1192">
                        <div className="flex items-baseline justify-center gap-2" data-id="element-1193">
                          <span className={`text-2xl font-bold ${step.darkColor}`} data-id="element-1194">
                            {step.stat}
                          </span>
                          <span className="text-xs text-[#6B7280] uppercase tracking-wide" data-id="element-1195">
                            {step.statLabel}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Decoración en esquina */}
                    <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-10 rounded-bl-full transition-opacity duration-500`} data-id="element-1196" />
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Mobile: Ultra compacto - MÁXIMO RESUMIDO */}
        <div className="md:hidden space-y-3" data-id="element-1197">
          {steps.map((step, index) => <div key={index} className="relative" data-id="element-1198">
              {/* Línea conectora vertical entre pasos */}
              {index < steps.length - 1 && <div className={`absolute left-5 top-12 w-0.5 h-full bg-gradient-to-b ${step.color} opacity-20 z-0`} data-id="element-1199" />}

              {/* Card ultra compacta */}
              <div className="relative bg-white rounded-lg p-3 shadow-sm border border-gray-100" data-id="element-1200">
                <div className="flex items-center gap-3" data-id="element-1201">
                  {/* Número e ícono combinados */}
                  <div className="flex-shrink-0" data-id="element-1202">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${step.color} flex items-center justify-center text-white shadow-sm`} data-id="element-1203">
                      <span className="text-base font-bold" data-id="element-1204">{step.number}</span>
                    </div>
                  </div>

                  {/* Contenido */}
                  <div className="flex-1 min-w-0" data-id="element-1205">
                    <h3 className="text-sm font-bold text-[#0A1628] mb-0.5" data-id="element-1206">
                      {step.mobileTitle}
                    </h3>
                    <p className="text-xs text-[#6B7280] leading-snug" data-id="element-1207">
                      {step.mobileDescription}
                    </p>
                  </div>

                  {/* Stat compacto */}
                  <div className="flex-shrink-0 text-right" data-id="element-1208">
                    <div className={`text-base font-bold ${step.darkColor}`} data-id="element-1209">
                      {step.stat}
                    </div>
                    <div className="text-xs text-[#6B7280]" data-id="element-1210">
                      {step.statLabel}
                    </div>
                  </div>
                </div>
              </div>
            </div>)}
        </div>

        {/* CTA final */}
        <div className="text-center mt-8 md:mt-16" data-id="element-1211">
          <button onClick={() => scrollToSection('calculadora')} className="group inline-flex items-center gap-3 px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-[#2B5FD9] to-[#1E4BB8] text-white text-sm md:text-lg font-semibold rounded-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 w-full md:w-auto justify-center" data-id="element-1212">
            <span data-id="element-1213">Evaluar ahora</span>
            <ArrowRightIcon className="h-4 w-4 md:h-5 md:w-5 group-hover:translate-x-1 transition-transform" data-id="element-1214" />
          </button>
          <p className="text-xs text-[#6B7280] mt-3 px-4" data-id="element-1215">
            Gratis · Sin compromiso · Resultados en minutos
          </p>
        </div>
      </div>
    </section>;
}